import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GripVertical } from 'lucide-react';
import { useAdminMenu, AdminMenuOrderedItem } from '@/contexts/AdminMenuContext';
import type { AdminMenuActionId } from '@/config/adminMenuCatalog';

interface AdminMenuListProps {
  onClose: () => void;
  onAction: (actionId: AdminMenuActionId) => void;
  iconSize?: number;
  rowClassName?: string;
}

const renderItemIcon = (icon: React.ReactNode, size: number, accent: string) => {
  if (React.isValidElement(icon)) {
    const props = (icon.props as { size?: number; className?: string }) || {};
    const className = `${accent} ${props.className ?? ''}`.trim();
    return React.cloneElement(icon as React.ReactElement<{ size?: number; className?: string }>, {
      size,
      className,
    });
  }
  return icon;
};

export const AdminMenuList: React.FC<AdminMenuListProps> = ({
  onClose,
  onAction,
  iconSize = 18,
  rowClassName = 'flex items-center gap-2 px-4 py-2 text-rs-off-white hover:bg-rs-jet-light transition-colors',
}) => {
  const { starredItems, reorderStarred } = useAdminMenu();
  const navigate = useNavigate();
  const [dragKey, setDragKey] = useState<string | null>(null);
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);

  if (starredItems.length === 0) return null;

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, key: string) => {
    setDragKey(key);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', key);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>, key: string) => {
    if (!dragKey || dragKey === key) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverKey(key);
  };

  const handleDragLeave = () => {
    setDragOverKey(null);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, targetKey: string) => {
    e.preventDefault();
    if (!dragKey || dragKey === targetKey) {
      setDragKey(null);
      setDragOverKey(null);
      return;
    }
    const keys = starredItems.map((it) => it.key);
    const fromIdx = keys.indexOf(dragKey);
    const toIdx = keys.indexOf(targetKey);
    if (fromIdx < 0 || toIdx < 0) {
      setDragKey(null);
      setDragOverKey(null);
      return;
    }
    keys.splice(fromIdx, 1);
    keys.splice(toIdx, 0, dragKey);
    reorderStarred(keys);
    setDragKey(null);
    setDragOverKey(null);
  };

  const handleDragEnd = () => {
    setDragKey(null);
    setDragOverKey(null);
  };

  const handleActivate = (item: AdminMenuOrderedItem) => {
    onClose();
    if (item.href) {
      navigate(item.href);
    } else if (item.actionId) {
      onAction(item.actionId);
    }
  };

  const renderRow = (item: AdminMenuOrderedItem) => {
    const isDragging = dragKey === item.key;
    const isDropTarget = dragOverKey === item.key && dragKey !== item.key;
    const wrapperClass = `flex items-stretch cursor-pointer ${isDragging ? 'opacity-40' : ''} ${
      isDropTarget ? 'border-t-2 border-rs-green' : 'border-t-2 border-transparent'
    }`;

    return (
      <div
        key={item.key}
        className={wrapperClass}
        role="button"
        draggable
        onDragStart={(e) => handleDragStart(e, item.key)}
        onDragOver={(e) => handleDragOver(e, item.key)}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e, item.key)}
        onDragEnd={handleDragEnd}
        onClick={() => handleActivate(item)}
      >
        <span
          className="flex items-center px-2 text-rs-off-white/30 hover:text-rs-off-white/70 cursor-grab active:cursor-grabbing"
          aria-label="Drag to reorder"
        >
          <GripVertical size={14} />
        </span>
        <div className={`${rowClassName} flex-1`}>
          {renderItemIcon(item.icon, iconSize, item.accent)}
          <span className="text-sm">{item.label}</span>
        </div>
      </div>
    );
  };

  return <div className="py-1">{starredItems.map(renderRow)}</div>;
};

export default AdminMenuList;
